import React from 'react';

/**
 * The date range selector on Growth Trends, Competitive Trends, Ranking Factors and AI Visibility
 * is created once by Header, so its state survives moving between those screens, but it is shown
 * on each screen itself, at the start of the screen's filter row.
 *
 * Header renders it through a portal into one shared DOM node. A screen marks where the selector
 * goes with <PageDateRangeSlot />, which holds that node for as long as the slot is mounted.
 * Moving the node, rather than rendering a selector per screen, keeps the single instance with
 * the same props and state it had when it lived in the top bar.
 */

let hostNode: HTMLDivElement | null = null;

// Slots currently on screen, oldest first. Normally there is exactly one.
const mountedSlots: HTMLElement[] = [];

export const getPageDateRangeHost = (): HTMLDivElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  if (!hostNode) {
    hostNode = document.createElement('div');
    hostNode.className = 'page-date-range-host';
  }
  return hostNode;
};

/** The shared node. Null on the server and on the first client render, so both render the same. */
export const usePageDateRangeHost = (): HTMLDivElement | null => {
  const [host, setHost] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setHost(getPageDateRangeHost());
  }, []);

  return host;
};

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

type PageDateRangeSlotProps = {
  /**
   * `filter-row` is the start of a screen's filter row, followed by a separator. `top-bar` keeps
   * the look the selector has in the app bar.
   */
  variant?: 'filter-row' | 'top-bar';
  className?: string;
};

export default function PageDateRangeSlot({ variant = 'filter-row', className = '' }: PageDateRangeSlotProps) {
  const slotRef = React.useRef<HTMLDivElement>(null);

  // A layout effect, so the selector is in place before the screen is painted.
  useIsomorphicLayoutEffect(() => {
    const slot = slotRef.current;
    const host = getPageDateRangeHost();
    if (!slot || !host) {
      return undefined;
    }

    mountedSlots.push(slot);
    slot.appendChild(host);

    return () => {
      mountedSlots.splice(mountedSlots.indexOf(slot), 1);
      // The next screen's slot may already hold the node, in which case there is nothing to undo.
      if (host.parentNode === slot) {
        slot.removeChild(host);
        const remainingSlot = mountedSlots[mountedSlots.length - 1];
        if (remainingSlot) {
          remainingSlot.appendChild(host);
        }
      }
    };
  }, []);

  return (
    <div
      ref={slotRef}
      className={`page-date-range-slot page-date-range-slot--${variant} ${className}`.trim()}
      data-testid="page-date-range-slot"
    />
  );
}
